<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { buildMail, feedback, technicalInfo, type FeedbackKind } from '../feedback';
import { t } from '../i18n';
import { useApp } from '../stores/app';
import Icon from './Icon.vue';
import Toggle from './Toggle.vue';

/**
 * "Report a problem": a message and, if the person wants, technical details; then their own mail
 * program opens with the letter ready (they read it all and send it themselves).
 */
const app = useApp();
const kind = ref<FeedbackKind>('bug');
const subject = ref('');
const message = ref('');
const attach = ref(true);
const info = ref('');
const showInfo = ref(false);
const field = ref<HTMLTextAreaElement | null>(null);
const KINDS: { id: FeedbackKind; icon: string }[] = [
  { id: 'bug', icon: 'warning' },
  { id: 'idea', icon: 'plus' },
  { id: 'question', icon: 'info' },
];

watch(
  () => feedback.open,
  async (o) => {
    if (!o) return;
    info.value = await technicalInfo();
    await nextTick();
    field.value?.focus();
  },
);

const mail = computed(() => (feedback.email ? buildMail(feedback.email, kind.value, message.value, attach.value ? info.value : null, subject.value) : null));
const ready = computed(() => message.value.trim().length > 0);

function close(): void {
  feedback.open = false;
}
/**
 * After the letter is opened the window can go; the text stays if the person comes back.
 * Close on the next turn: removing the link in this click makes Windows open the mail
 * program with an empty address.
 */
function opened(): void {
  app.notify('info', t('feedback.opened'), 6000);
  window.setTimeout(close, 0);
}
async function copy(): Promise<void> {
  if (!mail.value) return;
  await navigator.clipboard.writeText(`${feedback.email}\n${mail.value.subject}\n\n${mail.value.text}`).catch(() => undefined);
  app.notify('info', t('feedback.copied'));
}
</script>

<template>
  <div v-if="feedback.open && feedback.email" class="fb-backdrop" @pointerdown.self="close" @keydown.esc.stop="close">
    <div class="fb" role="dialog" aria-modal="true" aria-labelledby="fb-title" data-dialog="feedback">
      <header>
        <h2 id="fb-title">{{ t('feedback.title') }}</h2>
        <button class="icon-btn" :aria-label="t('common.close')" @click="close"><Icon name="close" :size="16" /></button>
      </header>
      <div class="scroll">
        <p class="to muted">{{ t('feedback.to') }} <strong data-field="recipient">{{ feedback.email }}</strong></p>

        <div class="seg-pills kinds" role="radiogroup" :aria-label="t('feedback.title')">
          <button v-for="k in KINDS" :key="k.id" role="radio" :aria-checked="kind === k.id" :class="{ active: kind === k.id }" :data-kind="k.id" @click="kind = k.id">
            <Icon :name="k.icon" :size="14" /><span class="seg-text">{{ t('feedback.kind.' + k.id) }}</span>
          </button>
        </div>

        <input v-model="subject" class="subject" type="text" maxlength="120" :placeholder="t('feedback.subjectPh')" :aria-label="t('feedback.subject')" data-field="subject" />

        <textarea ref="field" v-model="message" class="msg" rows="6" maxlength="3000" :placeholder="t('feedback.placeholder.' + kind)" :aria-label="t('feedback.title')" data-field="message"></textarea>

        <div class="attach">
          <div class="attach-text">
            <span class="attach-label">{{ t('feedback.attach') }}</span>
            <button v-if="attach" class="link" type="button" @click="showInfo = !showInfo">{{ showInfo ? t('feedback.hideInfo') : t('feedback.showInfo') }}</button>
          </div>
          <Toggle v-model="attach" data-field="attach" :label="t('feedback.attach')" />
        </div>
        <pre v-if="attach && showInfo" class="info" data-field="info">{{ info }}</pre>
        <p class="note muted">{{ t('feedback.note') }}</p>
        <p v-if="mail?.cut" class="note warn">{{ t('feedback.cut') }}</p>
      </div>

      <div class="actions">
        <button class="pill tall" type="button" :disabled="!ready" data-act="copy-feedback" @click="copy"><Icon name="copy" :size="14" />{{ t('feedback.copy') }}</button>
        <a class="pill primary tall send" :class="{ off: !ready }" :href="ready && mail ? mail.href : undefined" :aria-disabled="!ready" data-act="send-feedback" @click="ready ? opened() : $event.preventDefault()"><Icon name="external" :size="14" />{{ t('feedback.open') }}</a>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fb-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: grid;
  place-items: center;
  padding: 16px;
  background: var(--backdrop);
}
.fb {
  width: min(480px, 100%);
  max-height: min(640px, 100%);
  overflow: hidden;
  padding: 18px 20px;
  border-radius: var(--radius-panel);
  background: var(--panel);
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.scroll > * {
  flex-shrink: 0;
}
header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}
.to {
  margin: -6px 0 0;
  font-size: 12.5px;
  line-height: 1.4;
}
.to strong {
  color: var(--accent-text);
  font-weight: 600;
}
.kinds {
  align-self: stretch;
  display: flex;
  width: 100%;
}
.kinds button {
  flex: 1;
  gap: 6px;
}
.kinds button :deep(svg) {
  flex: none;
}
.subject {
  width: 100%;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--input);
  color: var(--text);
  font: inherit;
  font-size: 13.5px;
}
.subject:focus {
  border-color: var(--accent);
}
.msg {
  width: 100%;
  resize: vertical;
  min-height: 120px;
  padding: 10px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-panel);
  background: var(--input);
  color: var(--text);
  font: inherit;
  font-size: 13.5px;
  line-height: 1.5;
}
.msg:focus {
  border-color: var(--accent);
}
.attach {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 36px;
}
.attach-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.attach-label {
  font-size: 13.5px;
}
.link {
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  color: var(--accent-text);
  font: inherit;
  font-size: 12px;
  cursor: pointer;
}
.link:hover {
  color: var(--accent);
}
.info {
  margin: 0;
  max-height: 150px;
  overflow: auto;
  padding: 10px 12px;
  border-radius: var(--radius-panel);
  background: var(--code);
  border: 1px solid var(--border);
  font: 11.5px/1.5 ui-monospace, Consolas, monospace;
  white-space: pre-wrap;
  word-break: break-word;
  user-select: text;
}
.note {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
}
.warn {
  padding: 10px 12px;
  border-radius: var(--radius-panel);
  background: var(--warn-soft);
  color: var(--warn);
}
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}
.pill.tall {
  height: 36px;
  padding: 0 16px;
  font-size: 13px;
}
.send {
  text-decoration: none;
}
.send.off {
  opacity: 0.45;
  pointer-events: none;
}
</style>
